import React, { useState, useEffect, useRef, SyntheticEvent } from "react";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/system";
import { useTranslation } from "utils/locales/utilityFunctions";

interface HoldMenuItemProps {
  label: string;
  holdTime: number; // Hold time in milliseconds
  disabled?: boolean;
  onHoldComplete: () => void;
  onClick?: () => void;
}

const StyledMenuItem = styled(MenuItem)<{ progress: number }>(({ progress }) => ({
  background: `linear-gradient(to right, #dadada ${progress}%, transparent ${progress}%)`,
}));

const HoldMenuItem: React.FC<HoldMenuItemProps> = ({
  label,
  holdTime,
  disabled,
  onHoldComplete,
  onClick,
}) => {
  const { translate } = useTranslation();

  const [progress, setProgress] = useState(0);
  const [currentLabel, setCurrentLabel] = useState(label);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onHoldCompleteExecuted = useRef(false);

  useEffect(() => {
    if (progress >= 100 && !onHoldCompleteExecuted.current) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      onHoldCompleteExecuted.current = true;
      onHoldComplete();
      setCurrentLabel(label);
    }
  }, [progress, onHoldComplete, label]);

  const startHold = () => {
    setCurrentLabel(translate("button-hold"));
    onHoldCompleteExecuted.current = false;
    intervalRef.current = setInterval(() => {
      setProgress((prev) => Math.min(prev + 100 / (holdTime / 100), 100));
    }, 100);
  };

  const stopHold = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setProgress(0);
    setCurrentLabel(label);
  };

  const handleClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    if (onClick) onClick();
    setCurrentLabel(translate("button-hold"));
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = setTimeout(() => setCurrentLabel(label), 1000);
  };

  return (
    <StyledMenuItem
      progress={progress}
      disabled={disabled}
      // --- Web --- //
      onMouseDown={startHold}
      onMouseUp={stopHold}
      onMouseLeave={stopHold}
      // --- Mobile / Capacitor --- //
      onTouchStart={startHold}
      onTouchEnd={stopHold}
      onTouchCancel={stopHold}
      onClick={handleClick}
    >
      {currentLabel}
    </StyledMenuItem>
  );
};

export default HoldMenuItem;
